import { ReactNode, useCallback, useReducer } from 'react';

export interface Dialog {
  open: boolean;
  target?: ReactNode | string;
  header?: ReactNode | string;
  main?: ReactNode | string;
  footer?: ReactNode | (() => void);
  cancelText?: string;
  submitText?: string;
}

type DialogAction =
  | { type: 'HEADER'; payload: ReactNode | string }
  | { type: 'MAIN'; payload: ReactNode | string }
  | { type: 'FOOTER'; payload: ReactNode | (() => void) }
  | { type: 'OPEN'; payload: boolean }
  | { type: 'ALL'; payload: Dialog };

const dialogReducer = (state: Dialog, action: DialogAction): Dialog => {
  switch (action.type) {
    case 'HEADER':
      return { ...state, header: action.payload };
    case 'MAIN':
      return { ...state, main: action.payload };
    case 'FOOTER':
      return { ...state, footer: action.payload };
    case 'OPEN':
      return { ...state, open: action.payload };
    case 'ALL':
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

export const useDialog = (
  initialDialogProperty: Dialog = { open: false, header: '', main: '', footer: () => {} }
) => {
  const [dialog, dialogDispatch] = useReducer(dialogReducer, initialDialogProperty);

  const onToggle = useCallback(() => {
    dialogDispatch({ type: 'OPEN', payload: !dialog.open });
  }, [dialog.open]);

  const onOpen = useCallback(() => {
    dialogDispatch({ type: 'OPEN', payload: true });
  }, []);

  const onClose = useCallback(() => {
    dialogDispatch({ type: 'OPEN', payload: false });
  }, []);

  return {
    dialog,
    dialogDispatch,
    onToggle,
    onOpen,
    onClose,
  };
};
